import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Grid,
  Pagination,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack
} from "@mui/material";
import { EditOutlined, DeleteOutline, Speed as SpeedIcon, Add as AddIcon } from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, deleteDoc, Timestamp, query, where, orderBy } from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../../firebase";

const PAGE_SIZE = 10;

function Vehicalmgmt() {
  const [vehicles, setVehicles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  // Vehicle Form Modal state
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);

  // Fuel logs modal state
  const [fuelDialogOpen, setFuelDialogOpen] = useState(false);
  const [selectedVehicleLogs, setSelectedVehicleLogs] = useState([]);
  const [selectedVehicleNumber, setSelectedVehicleNumber] = useState("");
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [form, setForm] = useState({
    type: "Tipper",
    vehicalNo: "",
    puc: "",
    insurance: "",
    pucDate: "",
    insuranceDate: "",
    fitnessDate: "",
    roadTaxDate: "",
  });

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const snap = await getDocs(collection(db, "vehicles"));
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setVehicles(arr);
    } catch (err) {
      console.error("Error loading vehicles:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm({
      type: "Tipper",
      vehicalNo: "",
      puc: "",
      insurance: "",
      pucDate: "",
      insuranceDate: "",
      fitnessDate: "",
      roadTaxDate: "",
    });
    setEditingId(null);
  };

  const saveVehicle = async () => {
    if (!form.type || !form.vehicalNo) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    try {
      let id = editingId || doc(collection(db, "vehicles")).id;

      await setDoc(doc(db, "vehicles", id), {
        ...form,
        createdTime: editingId ? form.createdTime : Timestamp.now(),
      });

      Swal.fire("Success", editingId ? "Vehicle updated" : "Vehicle added", "success");

      resetForm();
      setVehicleModalOpen(false);
      loadVehicles();
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const editVehicle = (v) => {
    setForm({
      type: v.type || "Tipper",
      vehicalNo: v.vehicalNo || "",
      puc: v.puc || "",
      insurance: v.insurance || "",
      pucDate: v.pucDate || "",
      insuranceDate: v.insuranceDate || "",
      fitnessDate: v.fitnessDate || "",
      roadTaxDate: v.roadTaxDate || "",
      createdTime: v.createdTime || null,
    });
    setEditingId(v.id);
    setVehicleModalOpen(true);
  };

  const deleteVehicle = async (id) => {
    const res = await Swal.fire({
      title: "Delete vehicle?",
      icon: "warning",
      showCancelButton: true,
    });

    if (!res.isConfirmed) return;

    try {
      await deleteDoc(doc(db, "vehicles", id));
      Swal.fire("Deleted", "Vehicle removed", "success");
      loadVehicles();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  // Open Fuel Logs & Average check modal for specific vehicle (sorted date desc)
  const checkFuelAndAverage = async (vehicle) => {
    setSelectedVehicleNumber(vehicle.vehicalNo);
    setFuelDialogOpen(true);
    setLoadingLogs(true);
    try {
      const q = query(
        collection(db, "fuel_entries"),
        where("vehicleId", "==", vehicle.id)
      );
      const snap = await getDocs(q);
      const logsData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Sort fuel logs by date descending (newest first)
      logsData.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      
      setSelectedVehicleLogs(logsData);
    } catch (err) {
      console.error("Error fetching fuel logs for vehicle:", err);
      setSelectedVehicleLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const filtered = vehicles.filter(
    (v) =>
      v.type?.toLowerCase().includes(search.toLowerCase()) ||
      v.vehicalNo?.toLowerCase().includes(search.toLowerCase())
  );

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Box flex={1} p={3}>
        {/* ================= Top Bar ================= */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight={800}>
            Vehicle Management
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => { resetForm(); setVehicleModalOpen(true); }}
          >
            Add Vehicle
          </Button>
        </Stack>

        {/* ================= Search ================= */}
        <TextField
          label="Search by type or vehicle no"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          sx={{ mb: 2, bgcolor: '#fff', width: '300px' }}
          size="small"
        />

        {/* ================= Table ================= */}
        <Card>
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Sr No</b></TableCell>
                  <TableCell><b>Type</b></TableCell>
                  <TableCell><b>Vehicle No</b></TableCell>
                  <TableCell><b>PUC</b></TableCell>
                  <TableCell><b>Insurance</b></TableCell>
                  <TableCell><b>PUC Expiry</b></TableCell>
                  <TableCell><b>Insurance Expiry</b></TableCell>
                  <TableCell><b>Fitness Expiry</b></TableCell>
                  <TableCell><b>Road Tax Expiry</b></TableCell>
                  <TableCell align="center"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="textSecondary">No vehicles found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((v, index) => (
                    <TableRow key={v.id}>
                      <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                      <TableCell>{v.type}</TableCell>
                      <TableCell>{v.vehicalNo}</TableCell>
                      <TableCell>{v.puc || "-"}</TableCell>
                      <TableCell>{v.insurance || "-"}</TableCell>
                      <TableCell>{v.pucDate || "-"}</TableCell>
                      <TableCell>{v.insuranceDate || "-"}</TableCell>
                      <TableCell>{v.fitnessDate || "-"}</TableCell>
                      <TableCell>{v.roadTaxDate || "-"}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SpeedIcon fontSize="small" />}
                            onClick={() => checkFuelAndAverage(v)}
                            sx={{ fontSize: '0.7rem', py: 0.2, px: 1 }}
                          >
                            Fuel/Avg
                          </Button>
                          <IconButton color="primary" size="small" onClick={() => editVehicle(v)}>
                            <EditOutlined fontSize="small" />
                          </IconButton>
                          <IconButton color="error" size="small" onClick={() => deleteVehicle(v.id)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(filtered.length / PAGE_SIZE) || 1}
                page={page}
                onChange={(e, v) => setPage(v)}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* ================= Add / Edit Vehicle Modal ================= */}
      <Dialog open={vehicleModalOpen} onClose={() => setVehicleModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {editingId ? "Edit Vehicle" : "Add New Vehicle"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid className="row" spacing={2} sx={{ mt: 0.5 }}>
            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
              <TextField
                select
                label="Type"
                name="type"
                value={form.type}
                onChange={handleChange}
                fullWidth
                size="small"
              >
                <MenuItem value="Tipper">Tipper</MenuItem>
                <MenuItem value="Truck">Truck</MenuItem>
                <MenuItem value="Tractor">Tractor</MenuItem>
              </TextField>
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Vehicle No"
                name="vehicalNo"
                value={form.vehicalNo}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="PUC"
                name="puc"
                value={form.puc}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Insurance"
                name="insurance"
                value={form.insurance}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="PUC Expiry Date"
                name="pucDate"
                InputLabelProps={{ shrink: true }}
                value={form.pucDate}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Insurance Expiry Date"
                name="insuranceDate"
                InputLabelProps={{ shrink: true }}
                value={form.insuranceDate}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fitness Expiry Date"
                name="fitnessDate"
                InputLabelProps={{ shrink: true }}
                value={form.fitnessDate}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Road Tax Expiry Date"
                name="roadTaxDate"
                InputLabelProps={{ shrink: true }}
                value={form.roadTaxDate}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setVehicleModalOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={saveVehicle}>
            {editingId ? "Update Vehicle" : "Add Vehicle"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Fuel & Average History Dialog (Date Desc) ================= */}
      <Dialog open={fuelDialogOpen} onClose={() => setFuelDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Fuel & Average History (Latest First) — {selectedVehicleNumber}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {loadingLogs ? (
            <Typography align="center" py={4}>Loading fuel logs...</Typography>
          ) : selectedVehicleLogs.length === 0 ? (
            <Typography align="center" py={4} color="textSecondary">No fuel records found for this vehicle.</Typography>
          ) : (
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell><b>Driver</b></TableCell>
                  <TableCell><b>Open / Close Meter</b></TableCell>
                  <TableCell><b>Qty & Rate</b></TableCell>
                  <TableCell><b>Average</b></TableCell>
                  <TableCell><b>Total Amount</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedVehicleLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{log.date}</TableCell>
                    <TableCell>{log.personName || "-"}</TableCell>
                    <TableCell>{log.openingReading} / {log.closingReading}</TableCell>
                    <TableCell>{log.qty}L @ ₹{log.rate}</TableCell>
                    <TableCell>
                      <Typography color="secondary" fontWeight="bold">
                        {log.average} km/L
                      </Typography>
                    </TableCell>
                    <TableCell><b>₹{log.amount}</b></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFuelDialogOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Vehicalmgmt;